import React from 'react';
import { useNavigate } from 'react-location';
import Page from 'Core/components/Page';
import { PrimaryButton } from 'Core/components/Button';
import { Column, Row } from 'Core/components/Layout';
import { createLendFlowApplicationPath } from 'Modules/lendFlow/routes';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import { providerSequence } from 'Modules/lendFlow/config/providerConfig';

const HomeScreen = () => {
  document.title = 'Payment Portal - LendFlow';
  const navigate = useNavigate();
  const { data: paymentInfo } = useGetPaymentInfo();

  const creditRating = paymentInfo?.credit_rating_estimate;
  const matchedProviders = creditRating ? providerSequence[creditRating] : null;

  const handleStartApplication = () => {
    navigate({ to: createLendFlowApplicationPath, search: true });
  };

  return (
    <Page
      title="LendFlow Financing"
      subtitle="Global Multi-Provider Financing"
      withStepProgress={false}
      stickyFooter
      branding="lendflow"
    >
      <div className="max-w-2xl mx-auto py-8">
        {/* Hero Section */}
        <div className="rounded-2xl bg-gradient-to-br from-lendflow-bg to-lendflow-dark p-8 text-white mb-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-3">Multi-Provider Financing</h2>
          <p className="text-lg opacity-90 leading-relaxed">
            This flow is for the <span className="font-semibold">GLOBAL</span> financing option.
            Your application will be submitted to multiple financing providers sequentially, matched
            based on your credit profile to find the best available offer.
          </p>
        </div>

        {/* How It Works */}
        <div className="bg-lendflow-surface rounded-xl p-6 mb-8 border border-lendflow-accent/20">
          <h3 className="text-lg font-semibold text-lendflow-dark mb-4">How It Works</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-lendflow-bg text-white flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div>
                <p className="font-medium text-gray-800">Submit Your Application</p>
                <p className="text-sm text-gray-600">
                  Complete a single application with your personal and financial details.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-lendflow-bg text-white flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div>
                <p className="font-medium text-gray-800">Automated Provider Matching</p>
                <p className="text-sm text-gray-600">
                  Your application is evaluated by multiple financing providers based on your credit
                  score.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-lendflow-bg text-white flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div>
                <p className="font-medium text-gray-800">Best Offer Selection</p>
                <p className="text-sm text-gray-600">
                  You will receive the most favorable financing terms available to you.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Disclaimer:</span> By proceeding, you authorize us to
            submit your application to multiple financing providers. A soft credit inquiry may be
            performed initially, followed by a hard inquiry if you choose to accept an offer. Terms
            and approval are subject to each provider&apos;s criteria.
          </p>
        </div>

        {/* CTA */}
        <Row>
          <Column className="md:!flex-col-12 !flex !justify-center">
            <PrimaryButton
              onClick={handleStartApplication}
              data-testid="start-lendflow-application"
              className="!bg-lendflow-bg hover:!bg-lendflow-dark !text-white !px-8 !py-3 !rounded-lg !text-base !font-semibold !shadow-md hover:!shadow-lg !transition-all"
            >
              Start Financing Application
            </PrimaryButton>
          </Column>
        </Row>

        {/* TODO: DEV-TESTING ONLY - Remove this section before production */}
        <div className="mt-8 border-2 border-dashed border-red-400 rounded-xl p-6 bg-red-50">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
              DEV ONLY
            </span>
            <h3 className="text-lg font-semibold text-red-700">
              Provider Sequence (Credit Rating Debug)
            </h3>
          </div>
          <div className="space-y-2 text-sm">
            <p className="text-gray-700">
              <span className="font-medium">Credit Rating Estimate:</span>{' '}
              {creditRating ? (
                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{creditRating}</span>
              ) : (
                <span className="italic text-gray-400">Not available</span>
              )}
            </p>
            {matchedProviders ? (
              <div>
                <p className="font-medium text-gray-700 mb-2">Provider Order:</p>
                <ol className="list-decimal list-inside space-y-1">
                  {matchedProviders.map((provider) => (
                    <li
                      key={provider}
                      className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200"
                    >
                      {provider}
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              <p className="italic text-gray-400">
                {creditRating
                  ? `No provider sequence configured for rating: "${creditRating}"`
                  : 'Waiting for payment info...'}
              </p>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default HomeScreen;
